import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, map, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';

/** An export-id-type option allowed for a destination type. */
export interface ExportIdTypeOption {
  ID?: string;
  ExportIDType?: string;
  Name?: string;
}

export interface DestinationTypeDto {
  ID: number;
  Name?: string;
  ActivationType: string;
  DestinationGroup?: string;
  ActivationParamsType?: string;
  Description?: string;
  ConnectorType: number;
  ExportIDType: string;
  ExportIDTypeName?: string;
  /** Allowed export-id-types for this destination type (when the API returns the richer shape). */
  ExportIDTypes?: ExportIdTypeOption[];
  PermissionType?: 'self-service' | 'assisted-service';
}

/** A DCR/entity that owns (can use) a destination — `connectors.destination_list_permissions`. */
export interface DestinationOwnerPermission {
  EntityID: string;
}

export interface DestinationDto {
  ID: number;
  Name: string;
  DestinationTypeID: number;
  DestinationTypeName: string;
  ConnectorType: number;
  ConnectorName?: string;
  ActivationType: string;
  ActivationParamsType?: string;
  EntityType?: string;
  EntityID: string;
  DestinationGroup?: string;
  Status?: string;
  ExportIDType?: string;
  ExportIDTypeName?: string;
  EncryptedConnectionParamsJson?: string;
  EncryptedKey?: string;
  EncryptedIv?: string;
  Deleted?: number;
  IsActive?: number;
  IsSandbox?: number;
  Description?: string;
  /** DCRs/entities that own the destination (multi-DCR). */
  OwnerPermissions?: DestinationOwnerPermission[];
}

/** Body for POST /connectors/test-connection — tests connector params before saving. */
export interface TestConnectionRequest {
  ConnectorType: number;
  EncryptedConnectionParamsJson: string;
  EncryptedKey?: string;
  EncryptedIv?: string;
}

/** Server operation status returned by the connection-test endpoints. */
export interface ConnectionTestResult {
  status: 'OK' | 'ERROR';
  message?: string;
}

interface GenericResponse<T> {
  payload?: T;
  error?: {
    status: number;
    message: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class DestinationsApiService {
  private readonly fallbackBase = this.normalizeBase(environment.apiUrl);

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}

  getDestinationTypes(): Observable<DestinationTypeDto[]> {
    const endpoint = this.composeEndpoint('/webapp/crud/destination-type');
    return this.http.get<GenericResponse<DestinationTypeDto[]>>(
      endpoint.url,
      { headers: this.buildHeaders(endpoint.useRemote) }
    ).pipe(
      map(res => res.payload ?? []),
      catchError(error => {
        console.error('[destinations] types failed', error);
        return throwError(() => error);
      })
    );
  }

  getDestinations(options?: { skipDcr?: boolean; includeInactive?: boolean }): Observable<DestinationDto[]> {
    // Admin list wants inactive destinations too; the user list keeps the default (active only).
    const path = options?.includeInactive
      ? '/webapp/crud/destinations?IncludeInactive=true'
      : '/webapp/crud/destinations';
    const endpoint = this.composeEndpoint(path);
    return this.http.get<GenericResponse<DestinationDto[]>>(
      endpoint.url,
      { headers: this.buildHeaders(endpoint.useRemote, options) }
    ).pipe(
      map(res => res.payload ?? []),
      catchError(error => {
        console.error('[destinations] list failed', error);
        return throwError(() => error);
      })
    );
  }

  /** Test a set of connector params before saving (POST /connectors/test-connection). */
  testConnection(body: TestConnectionRequest): Observable<ConnectionTestResult> {
    const endpoint = this.composeEndpoint('/webapp/connectors/test-connection');
    return this.http.post<GenericResponse<ConnectionTestResult>>(
      endpoint.url,
      body,
      { headers: this.buildHeaders(endpoint.useRemote) }
    ).pipe(
      map(res => this.requireTestResult(res)),
      catchError(error => {
        console.error('[destinations] test-connection failed', error);
        return throwError(() => error);
      })
    );
  }

  /** Test the saved connection of an existing destination (GET /connectors/test-saved-connection). */
  testSavedConnection(destinationId: number): Observable<ConnectionTestResult> {
    const endpoint = this.composeEndpoint(`/webapp/connectors/test-saved-connection?DestinationID=${destinationId}`);
    return this.http.get<GenericResponse<ConnectionTestResult>>(
      endpoint.url,
      { headers: this.buildHeaders(endpoint.useRemote) }
    ).pipe(
      map(res => this.requireTestResult(res)),
      catchError(error => {
        console.error('[destinations] test-saved-connection failed', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Fetch the saved connection params of a destination, AES-re-encrypted with the client's
   * ephemeral key/iv (POST /connectors/get-connection-params). The returned base64 string is
   * decrypted client-side with `ClientCrypto.decryptAES`.
   */
  getConnectionParams(body: { DestinationID: number; EncryptedKey: string; EncryptedIv: string }): Observable<string> {
    const endpoint = this.composeEndpoint('/webapp/connectors/get-connection-params');
    return this.http.post<GenericResponse<string>>(
      endpoint.url,
      body,
      { headers: this.buildHeaders(endpoint.useRemote) }
    ).pipe(
      map(res => {
        if (res.payload == null) {
          throw new Error(res.error?.message || 'No connection params returned');
        }
        return res.payload;
      }),
      catchError(error => {
        console.error('[destinations] get-connection-params failed', error);
        return throwError(() => error);
      })
    );
  }

  private requireTestResult(res: GenericResponse<ConnectionTestResult>): ConnectionTestResult {
    if (res.payload) {
      return res.payload;
    }
    return { status: 'ERROR', message: res.error?.message || 'No response from connection test' };
  }

  createDestination(payload: Partial<DestinationDto>): Observable<DestinationDto> {
    const endpoint = this.composeEndpoint('/webapp/crud/destinations');
    return this.http.post<GenericResponse<DestinationDto>>(
      endpoint.url,
      payload,
      { headers: this.buildHeaders(endpoint.useRemote) }
    ).pipe(
      map(res => {
        if (!res.payload) {
          throw new Error('Unable to create destination');
        }
        return res.payload;
      }),
      catchError(error => {
        console.error('[destinations] create failed', error);
        return throwError(() => error);
      })
    );
  }

  updateDestination(payload: Partial<DestinationDto>): Observable<void> {
    const endpoint = this.composeEndpoint('/webapp/crud/destinations');
    return this.http.put<GenericResponse<string>>(
      endpoint.url,
      payload,
      { headers: this.buildHeaders(endpoint.useRemote) }
    ).pipe(
      map(res => {
        if (res.error) {
          throw new Error(res.error.message || 'Unable to update destination');
        }
      }),
      catchError(error => {
        console.error('[destinations] update failed', error);
        return throwError(() => error);
      })
    );
  }

  deleteDestination(destinationId: number): Observable<void> {
    const endpoint = this.composeEndpoint(`/webapp/crud/destinations?DestinationID=${destinationId}`);
    return this.http.delete<GenericResponse<DestinationDto>>(
      endpoint.url,
      { headers: this.buildHeaders(endpoint.useRemote) }
    ).pipe(
      map(() => undefined),
      catchError(error => {
        console.error('[destinations] delete failed', error);
        return throwError(() => error);
      })
    );
  }

  private composeEndpoint(path: string): { url: string; useRemote: boolean } {
    const info = this.getBaseUrlInfo();
    if (!path.startsWith('/')) {
      path = `/${path}`;
    }
    return {
      url: `${info.baseUrl}${path}`,
      useRemote: info.useRemote
    };
  }

  private buildHeaders(useRemote: boolean, options?: { skipDcr?: boolean }): HttpHeaders {
    if (useRemote) {
      const token = this.authService.getTncApiToken();
      let remoteHeaders = new HttpHeaders({ 'Content-Type': 'application/json' });
      if (token) {
        remoteHeaders = remoteHeaders.set('Authorization', token);
      }
      return remoteHeaders;
    }

    let headers = new HttpHeaders(this.authService.getAuthHeaders());
    headers = headers.set('Content-Type', 'application/json');
    return headers;
  }

  private getBaseUrlInfo(): { baseUrl: string; useRemote: boolean } {
    const remoteBase = this.authService.getTncApiBaseUrl();
    const remoteToken = this.authService.getTncApiToken();
    if (remoteBase && remoteToken) {
      return { baseUrl: this.normalizeBase(remoteBase), useRemote: true };
    }
    return { baseUrl: this.fallbackBase, useRemote: false };
  }

  private normalizeBase(url: string): string {
    if (!url) {
      return '';
    }
    return url.replace(/\/+$/u, '');
  }
}
